import { ReactNode } from 'react';

import { CrmUnavailable } from '@/crm/presentation/CrmUnavailable';
import { useCanAccessCrm } from '@/crm/application/useCanAccessCrm';

/**
 * LLAMS-465 — gates the /crm/* route behind the canonical useCanAccessCrm() rule.
 * Placed OUTSIDE the lazy-loaded <CrmRoutes/> subtree deliberately: because that
 * component is React.lazy()-imported, denying here means the module (and its
 * module-scope prefetch + CRM portal creation) never even loads for a denied user
 * — not just that the resulting UI is hidden.
 *
 * This is UX defense-in-depth only. CRMAPI (LLAMS-464) remains the authoritative,
 * security-relevant enforcement boundary — this guard must never be treated as a
 * substitute for server-side authorization.
 */
const CrmAccessGuard = ({ children }: { children: ReactNode }) => {
  const { allowed, isReady } = useCanAccessCrm();

  if (!isReady) {
    // Matches the sibling <Suspense fallback={null}> convention already used for
    // this route — a brief null render while auth/LD identity settle, not a denial.
    return null;
  }

  if (!allowed) {
    return <CrmUnavailable />;
  }

  return <>{children}</>;
};

export default CrmAccessGuard;
